import React, { useRef, useState } from 'react';
import { DeleteOutlined, PictureOutlined, SyncOutlined } from '@ant-design/icons';
import ImageCropModal from '../../commonComponents/ImageCropModal';
import { notification } from '../../commonComponents/Notification/Notification';
import { FILE_CONSTRAINTS, IMAGE_FILE_HINT, toSafeImageUrl } from './constants';
import {
  DeleteImageButton,
  HiddenFileInput,
  ImageActionButtons,
  ImageEmptyIcon,
  ImageEmptyState,
  ImageEmptyText,
  ImageHint,
  ImagePreviewImg,
  ImagePreviewWrapper,
  ImageUploadButton,
} from './styles';

interface StoreImageUploadFieldProps {
  imageFile: File | null;
  existingImageUrl: string;
  isDarkMode?: boolean;
  onFileSelected: (file: File) => void;
  onDelete: () => void;
}

const StoreImageUploadField: React.FC<StoreImageUploadFieldProps> = ({
  imageFile,
  existingImageUrl,
  isDarkMode,
  onFileSelected,
  onDelete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  // `existingImageUrl` traces back to the API's `imageUrl`, which is typed
  // but not guaranteed at runtime — re-sanitize here so a malformed value
  // never reaches an <img src>.
  const safeExistingImageUrl = toSafeImageUrl(existingImageUrl);
  const hasImage = !!imageFile || !!safeExistingImageUrl;
  const imageSrc = imageFile ? URL.createObjectURL(imageFile) : safeExistingImageUrl || null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!(FILE_CONSTRAINTS.ALLOWED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
      notification.error('Unsupported File', 'Please select a JPG, PNG, GIF, or WEBP image.', 5);
      return;
    }
    if (file.size > FILE_CONSTRAINTS.MAX_IMAGE_SIZE) {
      notification.error('Image Too Large', 'Please select an image smaller than 10 MB.', 5);
      return;
    }
    if (inputRef.current) inputRef.current.value = '';
    setCropSrc(URL.createObjectURL(file));
  };

  return (
    <>
      <ImagePreviewWrapper $isDark={isDarkMode}>
        {hasImage && imageSrc ? (
          <>
            <ImagePreviewImg
              src={imageSrc}
              alt=""
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <ImageActionButtons>
              <ImageUploadButton onClick={() => inputRef.current?.click()}>
                <SyncOutlined />
                Change image
              </ImageUploadButton>
              <DeleteImageButton icon={<DeleteOutlined />} onClick={onDelete} size="small" danger />
            </ImageActionButtons>
          </>
        ) : (
          <ImageEmptyState onClick={() => inputRef.current?.click()}>
            <ImageEmptyIcon>
              <PictureOutlined />
            </ImageEmptyIcon>
            <ImageEmptyText>No image selected</ImageEmptyText>
            <ImageUploadButton onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>
              + Add image
            </ImageUploadButton>
          </ImageEmptyState>
        )}

        <HiddenFileInput type="file" accept="image/*" ref={inputRef} onChange={handleFileChange} />
      </ImagePreviewWrapper>
      <ImageHint>{IMAGE_FILE_HINT}</ImageHint>

      {cropSrc && (
        <ImageCropModal
          open={!!cropSrc}
          imageSrc={cropSrc}
          onCropDone={(file: File) => {
            setCropSrc(null);
            onFileSelected(file);
          }}
          onCancel={() => setCropSrc(null)}
        />
      )}
    </>
  );
};

export default StoreImageUploadField;
